import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, EMPTY, skip, Subject, switchMap } from 'rxjs';
import { SimulationDao } from '@app/dao/simulation/simulation.dao';
import { LifeInsurrancePackService } from './resolvers/simulation-pack-av.resolver';
import { PerPackService } from './resolvers/simulation-pack-per.resolver';
import { SCPIPackService } from './resolvers/simulation-pack-scpi.resolver';
import {
  containsPack,
  PackService,
  SimulationPack,
  SimulationPackService,
} from './simulation-pack.service';
import { PrFormService } from '@app/components/pr-form/pr-form.service';
import { ConfigurationService } from '@app/configuration/configuration.service';
import { EaSimulationScpiPackFormComponent } from '@app/forms/simulation/ea-simulation-scpi-pack-form/ea-simulation-scpi-pack-form.component';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { EaSimulationAvPackFormComponent } from '@app/forms/simulation/ea-simulation-av-pack-form/ea-simulation-av-pack-form.component';
import { EaSimulationPerPackFormComponent } from '@app/forms/simulation/ea-simulation-per-pack-form/ea-simulation-per-pack-form.component';

export interface SimuPackBody {
  choices?: any;
  title?: string;
  labelBtnManual?: string;
  content?: string;
  subContent?: string;
  type?: string;
}
@UntilDestroy()
@Component({
    selector: 'ea-simulation-packs',
    templateUrl: './ea-simulation-packs.component.html',
    styleUrls: ['./ea-simulation-packs.component.scss'],
    providers: [SimulationPackService],
    standalone: false
})
export class EaSimulationPacksComponent implements OnInit {
  @Input() manualPackDescription: string;
  @Input() manualPackButtonText: string;
  @Input() choices: boolean = false;
  @Input() set simulation(simulation) {
    this._simulation = simulation;
    if (
      this.initialized &&
      simulation?.parameters?.cashOrCredit !==
        this.simulationPackService.cashOrCredit
    ) {
      this.reloadPacks();
    }
  }
  get simulation() {
    return this._simulation;
  }
  private _simulation;
  private initialized = false;
  private readonly packsReload$ = new Subject<void>();
  @Input() withoutManual: boolean = false;
  @Input() isDisplay: boolean = true;
  @Input() isDisplayManual: boolean = true;
  @Output() onEditPack: EventEmitter<any> = new EventEmitter();
  @Output() packSelected: EventEmitter<SimulationPack> = new EventEmitter();
  @Input() set packService(v) {
    this.type = v;
    this._packService = this.findServiceByName(v);
  }
  @Input() withCustomPack = true;
  @Input() withPanelTitle = true;
  @Input() suitableBaseOnScore = undefined;
  @Input() withTable = true;
  user: UserAuthentified;
  get packService(): any {
    return this._packService;
  }
  _packService;
  packForm = EaSimulationScpiPackFormComponent;
  packFormAv = EaSimulationAvPackFormComponent;
  packFormPer = EaSimulationPerPackFormComponent;
  get panelTitle(): string {
    return this.simulationPackService.template.panelTitle;
  }
  @Input() isAutomatic: boolean = true;
  @Input() isManual: boolean = false;
  darkMode: boolean = false;
  allocation: any;
  imgSrc: string = 'assets/img/choose-pack-light.png';
  type: string = '';
  @Output() choiceChanged = new EventEmitter<string>();
  choiceOptions: string[][] = [];
  private readonly organizationName =
    this.configurationService.getOrganizationName();
  bodyAv: SimuPackBody = {
    choices: [
      ['selected', `Je choisis une allocation ${this.organizationName}`],
      ['manual', 'Je m’aventure seul'],
    ],
    title: 'Sélectionnez vos supports',
    content: 'Parmi plus de 700 supports disponibles',
    subContent: 'Unité de compte, obligations et ETF, fonds immobilier',
    labelBtnManual: 'Ajouter vos supports',
    type: 'life_insurance',
  };

  bodySCPI: SimuPackBody = {
    choices: [
      ['selected', `Je choisis un Pack ${this.organizationName}`],
      ['manual', 'Je choisis moi-même mes SCPI'],
    ],
    title: 'Sélectionnez vos SCPI',
    content: 'Parmi notre sélection des 100 meilleures SCPI',
    subContent: 'de rendement et fiscale du marché',
    labelBtnManual: 'Ajouter vos SCPI',
    type: 'scpi',
  };

  bodyPER: SimuPackBody = {
    choices: [
      ['selected', `Je choisis un Pack ${this.organizationName}`],
      ['manual', 'Je m’aventure seul'],
    ],
    title: 'Sélectionnez vos supports',
    content: 'Parmi plus de 700 supports disponibles',
    subContent: 'Unité de compte, obligations et ETF, fonds immobilier',
    labelBtnManual: 'Ajouter vos supports',
    type: 'per',
  };
  onChoiceSelected(event, type?: any, onSubmitFormCallback?: any) {
    if (event.id == 'selected') {
      this.isAutomatic = true;
      this.isManual = false;
    } else {
      this.isAutomatic = false;
      this.isManual = true;
      this.selectCustomPack(onSubmitFormCallback, type);
    }
    this.emitChoice();
  }

  emitChoice() {
    const choice = this.isAutomatic ? 'selected' : 'manual';
    this.choiceChanged.emit(choice);
  }
  constructor(
    private simulationPackService: SimulationPackService,
    private simulationDao: SimulationDao,
    private prFormService: PrFormService,
    private configurationService: ConfigurationService,
  ) {
    this.detectDarkMode();
  }

  ngOnInit() {
    this.emitChoice();
    this.simulationPackService.selectConfig(
      new this.packService(this.simulationDao),
    );
    this.choiceOptions = this.getTypeObject()?.choices ?? [];
    this.packsReload$
      .pipe(
        switchMap(() =>
          this.simulationPackService.getPacks().pipe(catchError(() => EMPTY)),
        ),
        untilDestroyed(this),
      )
      .subscribe();
    this.applySimulationCashOrCredit();
    this.initialized = true;
    this.simulationPackService.packsChangedSubject
      .pipe(skip(1), untilDestroyed(this))
      .subscribe(packs => this.offerOnlyManualChoiceWithoutPack(packs));
    const htmlElement = document.querySelector('html');
    const observer = new MutationObserver(() => {
      this.detectDarkMode();
    });
    observer.observe(htmlElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  private reloadPacks() {
    this.applySimulationCashOrCredit();
    this.packsReload$.next();
  }

  private applySimulationCashOrCredit() {
    this.simulationPackService.cashOrCredit =
      this._simulation?.parameters?.cashOrCredit;
  }

  private offerOnlyManualChoiceWithoutPack(packs: SimulationPack[]) {
    if (!this.choices) return;
    const allChoices = this.getTypeObject()?.choices ?? [];
    const hasPacks = containsPack(packs);
    const choiceOptions = hasPacks
      ? allChoices
      : allChoices.filter(([id]) => id === 'manual');
    if (choiceOptions.length === this.choiceOptions.length) return;

    this.choiceOptions = choiceOptions;
    this.isAutomatic = hasPacks;
    this.isManual = !hasPacks;
    this.emitChoice();
  }

  getTypeObject() {
    if (this.type === 'life_insurance') {
      return this.bodyAv;
    } else if (this.type === 'scpi') {
      return this.bodySCPI;
    } else if (this.type === 'per') {
      return this.bodyPER;
    }
    return null;
  }
  detectDarkMode() {
    const htmlElement = document.querySelector('html');
    this.darkMode = htmlElement.classList.contains('theme-dark');
    this.imgSrc = this.darkMode
      ? 'assets/img/choose-pack-dark.png'
      : 'assets/img/choose-pack-light.png';
  }
  onPackSelected(pack: SimulationPack) {
    this.simulationPackService.selectPack(pack);
    this.packSelected.emit(pack);
  }

  get selectedPack() {
    return this.simulationPackService.selectedPack;
  }

  findServiceByName(name) {
    let service: any;
    switch (name) {
      case 'life_insurance':
        service = LifeInsurrancePackService;
        break;
      case 'scpi':
        service = SCPIPackService;
        break;
      case 'per':
        service = PerPackService;
        break;
    }
    return service;
  }
  handleFormSubmission = (allocation: any) => {
    this.allocation = allocation;
    this.onEditPack.emit(this.allocation);
  };
  selectCustomPack(onSubmitFormCallback: any, type?: any) {
    if (type == 'scpi') {
      this.prFormService.trigger(
        this.packForm,
        [],
        onSubmitFormCallback,
        this.simulation.parameters,
      );
    }
    if (type == 'life_insurance') {
      this.prFormService.trigger(
        this.packFormAv,
        [],
        onSubmitFormCallback,
        this.simulation.parameters,
      );
    }
    if (type == 'per') {
      this.prFormService.trigger(
        this.packFormPer,
        [],
        onSubmitFormCallback,
        this.simulation.parameters,
      );
    }
  }
}
